/* eslint-disable react-refresh/only-export-components -- arquivo de roteamento exporta `router` (não-componente) por design */
import { Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AppLayout } from "@/app/layout/AppLayout";
import { AuthLayout } from "@/app/layout/AuthLayout";
import { RequireRole } from "@/app/guards/RequireRole";
import { RouteErrorBoundary } from "@/app/RouteErrorBoundary";
import { Skeleton } from "@/components/ui/skeleton";
import { lazyWithRetry } from "@/lib/lazy-with-retry";

// Papéis com acesso à área financeira (ledger, saques, pagamentos, faturamento).
const FINANCE = ["super_admin", "financial"] as const;
// Área exclusiva de super admin (usuários admin, configurações globais).
const PLATFORM = ["super_admin"] as const;

const LoginPage = lazyWithRetry(() =>
  import("@/features/auth/pages/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const DashboardPage = lazyWithRetry(() =>
  import("@/features/dashboard/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const MetricsPage = lazyWithRetry(() =>
  import("@/features/metrics/pages/MetricsPage").then((m) => ({ default: m.MetricsPage })),
);
const OrganizationsPage = lazyWithRetry(() =>
  import("@/features/organizations/pages/OrganizationsPage").then((m) => ({
    default: m.OrganizationsPage,
  })),
);
const BrokersPage = lazyWithRetry(() =>
  import("@/features/brokers/pages/BrokersPage").then((m) => ({ default: m.BrokersPage })),
);
const DevelopersPage = lazyWithRetry(() =>
  import("@/features/developers/pages/DevelopersPage").then((m) => ({ default: m.DevelopersPage })),
);
const PlansPage = lazyWithRetry(() =>
  import("@/features/plans/pages/PlansPage").then((m) => ({ default: m.PlansPage })),
);
const LegalPage = lazyWithRetry(() =>
  import("@/features/legal/pages/LegalPage").then((m) => ({ default: m.LegalPage })),
);
const SubscriptionsPage = lazyWithRetry(() =>
  import("@/features/subscriptions/pages/SubscriptionsPage").then((m) => ({
    default: m.SubscriptionsPage,
  })),
);
const PaymentsPage = lazyWithRetry(() =>
  import("@/features/payments/pages/PaymentsPage").then((m) => ({ default: m.PaymentsPage })),
);
const PriceAdjustmentsPage = lazyWithRetry(() =>
  import("@/features/pricing/pages/PriceAdjustmentsPage").then((m) => ({
    default: m.PriceAdjustmentsPage,
  })),
);
const BillingDashboardPage = lazyWithRetry(() =>
  import("@/features/billing/pages/BillingDashboardPage").then((m) => ({
    default: m.BillingDashboardPage,
  })),
);
const AffiliatesPage = lazyWithRetry(() =>
  import("@/features/affiliates/pages/AffiliatesPage").then((m) => ({
    default: m.AffiliatesPage,
  })),
);
const AffiliatePayoutsPage = lazyWithRetry(() =>
  import("@/features/affiliates/pages/AffiliatePayoutsPage").then((m) => ({
    default: m.AffiliatePayoutsPage,
  })),
);
const AffiliateProgramsPage = lazyWithRetry(() =>
  import("@/features/affiliates/pages/AffiliateProgramsPage").then((m) => ({
    default: m.AffiliateProgramsPage,
  })),
);
const AffiliateReportPage = lazyWithRetry(() =>
  import("@/features/affiliates/pages/AffiliateReportPage").then((m) => ({
    default: m.AffiliateReportPage,
  })),
);
const WithdrawalsPage = lazyWithRetry(() =>
  import("@/features/withdrawals/pages/WithdrawalsPage").then((m) => ({
    default: m.WithdrawalsPage,
  })),
);
const LedgerPage = lazyWithRetry(() =>
  import("@/features/ledger/pages/LedgerPage").then((m) => ({ default: m.LedgerPage })),
);
const AdminUsersPage = lazyWithRetry(() =>
  import("@/features/admin-users/pages/AdminUsersPage").then((m) => ({ default: m.AdminUsersPage })),
);
const AuditPage = lazyWithRetry(() =>
  import("@/features/audit/pages/AuditPage").then((m) => ({ default: m.AuditPage })),
);
const SupportPage = lazyWithRetry(() =>
  import("@/features/support/pages/SupportPage").then((m) => ({ default: m.SupportPage })),
);
const SupportTicketPage = lazyWithRetry(() =>
  import("@/features/support/pages/SupportTicketPage").then((m) => ({
    default: m.SupportTicketPage,
  })),
);
const VimeoIntegrationPage = lazyWithRetry(() =>
  import("@/features/integrations/pages/VimeoIntegrationPage").then((m) => ({ default: m.VimeoIntegrationPage })),
);
const SiteContentTestimonialsPage = lazyWithRetry(() =>
  import("@/features/site-content/pages/SiteContentPages").then((m) => ({ default: m.TestimonialsContentPage })),
);
const SiteContentInstitutionalPage = lazyWithRetry(() =>
  import("@/features/site-content/pages/SiteContentPages").then((m) => ({ default: m.InstitutionalContentPage })),
);
const SiteContentHowtoPage = lazyWithRetry(() =>
  import("@/features/site-content/pages/SiteContentPages").then((m) => ({ default: m.HowtoContentPage })),
);
const OpeningVideoPage = lazyWithRetry(() =>
  import("@/features/site-content/pages/OpeningVideoPage").then((m) => ({ default: m.OpeningVideoPage })),
);
const AppPromoPage = lazyWithRetry(() =>
  import("@/features/site-content/pages/AppPromoPage").then((m) => ({ default: m.AppPromoPage })),
);
const SettingsPage = lazyWithRetry(() =>
  import("@/features/settings/pages/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const NotificationsPage = lazyWithRetry(() =>
  import("@/features/notifications/pages/NotificationsPage").then((m) => ({
    default: m.NotificationsPage,
  })),
);
const ProfilePage = lazyWithRetry(() =>
  import("@/features/profile/pages/ProfilePage").then((m) => ({ default: m.ProfilePage })),
);
const FinanceDashboardPage = lazyWithRetry(() =>
  import("@/features/finance/pages/FinanceDashboardPage").then((m) => ({
    default: m.FinanceDashboardPage,
  })),
);
const FinanceReportsPage = lazyWithRetry(() =>
  import("@/features/finance/pages/FinanceReportsPage").then((m) => ({ default: m.FinanceReportsPage })),
);
const FinanceEntriesPage = lazyWithRetry(() =>
  import("@/features/finance/pages/FinanceEntriesPage").then((m) => ({ default: m.FinanceEntriesPage })),
);
const ReferralsPage = lazyWithRetry(() =>
  import("@/features/referrals/pages/ReferralsPage").then((m) => ({ default: m.ReferralsPage })),
);
const CampaignsPage = lazyWithRetry(() =>
  import("@/features/referrals/pages/CampaignsPage").then((m) => ({ default: m.CampaignsPage })),
);
const AccountInvitesPage = lazyWithRetry(() =>
  import("@/features/account-invites/pages/AccountInvitesPage").then((m) => ({ default: m.AccountInvitesPage })),
);

function PageFallback() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-8 w-64" />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
      <Skeleton className="h-96" />
    </div>
  );
}

function Lazy({ children }: { children: ReactNode }) {
  return (
    <RouteErrorBoundary pendingFallback={<PageFallback />}>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </RouteErrorBoundary>
  );
}

export const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    children: [{ path: "/login", element: <Lazy><LoginPage /></Lazy> }],
  },
  {
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: "/dashboard", element: <Lazy><DashboardPage /></Lazy> },
      { path: "/metrics", element: <Lazy><MetricsPage /></Lazy> },
      { path: "/organizations", element: <Lazy><OrganizationsPage /></Lazy> },
      { path: "/brokers", element: <Lazy><BrokersPage /></Lazy> },
      { path: "/referrals", element: <Lazy><ReferralsPage /></Lazy> },
      { path: "/referrals/campaigns", element: <Lazy><CampaignsPage /></Lazy> },
      { path: "/account-invites", element: <Lazy><AccountInvitesPage /></Lazy> },
      { path: "/developers", element: <Lazy><DevelopersPage /></Lazy> },
      { path: "/plans", element: <Lazy><PlansPage /></Lazy> },
      { path: "/legal", element: <Lazy><LegalPage /></Lazy> },
      { path: "/billing", element: <RequireRole anyOf={[...FINANCE]}><Lazy><BillingDashboardPage /></Lazy></RequireRole> },
      { path: "/subscriptions", element: <Lazy><SubscriptionsPage /></Lazy> },
      { path: "/price-adjustments", element: <RequireRole anyOf={[...FINANCE]}><Lazy><PriceAdjustmentsPage /></Lazy></RequireRole> },
      { path: "/payments", element: <RequireRole anyOf={[...FINANCE]}><Lazy><PaymentsPage /></Lazy></RequireRole> },
      { path: "/withdrawals", element: <RequireRole anyOf={[...FINANCE]}><Lazy><WithdrawalsPage /></Lazy></RequireRole> },
      // A fila de cadastros é leitura para qualquer admin; as ações de aprovar/recusar
      // são gateadas dentro da página por `hasRole`. Já saque e campanha mexem em
      // dinheiro e ficam fechados para quem não é financeiro.
      { path: "/affiliates", element: <Lazy><AffiliatesPage /></Lazy> },
      { path: "/affiliates/payouts", element: <RequireRole anyOf={[...FINANCE]}><Lazy><AffiliatePayoutsPage /></Lazy></RequireRole> },
      { path: "/affiliates/programs", element: <RequireRole anyOf={[...FINANCE]}><Lazy><AffiliateProgramsPage /></Lazy></RequireRole> },
      { path: "/affiliates/report", element: <Lazy><AffiliateReportPage /></Lazy> },
      { path: "/finance", element: <RequireRole anyOf={[...FINANCE]}><Lazy><FinanceDashboardPage /></Lazy></RequireRole> },
      { path: "/finance/entries", element: <RequireRole anyOf={[...FINANCE]}><Lazy><FinanceEntriesPage /></Lazy></RequireRole> },
      { path: "/finance/reports", element: <RequireRole anyOf={[...FINANCE]}><Lazy><FinanceReportsPage /></Lazy></RequireRole> },
      { path: "/ledger", element: <RequireRole anyOf={[...FINANCE]}><Lazy><LedgerPage /></Lazy></RequireRole> },
      { path: "/admin-users", element: <RequireRole anyOf={[...PLATFORM]}><Lazy><AdminUsersPage /></Lazy></RequireRole> },
      { path: "/audit", element: <Lazy><AuditPage /></Lazy> },
      { path: "/support", element: <Lazy><SupportPage /></Lazy> },
      { path: "/support/tickets/:id", element: <Lazy><SupportTicketPage /></Lazy> },
      { path: "/settings", element: <RequireRole anyOf={[...PLATFORM]}><Lazy><SettingsPage /></Lazy></RequireRole> },
      { path: "/integrations/vimeo", element: <RequireRole anyOf={[...PLATFORM]}><Lazy><VimeoIntegrationPage /></Lazy></RequireRole> },
      { path: "/site-content/opening", element: <RequireRole anyOf={[...PLATFORM]}><Lazy><OpeningVideoPage /></Lazy></RequireRole> },
      { path: "/site-content/testimonials", element: <RequireRole anyOf={[...PLATFORM]}><Lazy><SiteContentTestimonialsPage /></Lazy></RequireRole> },
      { path: "/site-content/institutional", element: <RequireRole anyOf={[...PLATFORM]}><Lazy><SiteContentInstitutionalPage /></Lazy></RequireRole> },
      { path: "/site-content/howto", element: <RequireRole anyOf={[...PLATFORM]}><Lazy><SiteContentHowtoPage /></Lazy></RequireRole> },
      { path: "/site-content/app", element: <RequireRole anyOf={[...PLATFORM]}><Lazy><AppPromoPage /></Lazy></RequireRole> },
      { path: "/notifications", element: <Lazy><NotificationsPage /></Lazy> },
      { path: "/profile", element: <Lazy><ProfilePage /></Lazy> },
    ],
  },
  { path: "*", element: <Navigate to="/dashboard" replace /> },
]);
